import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { VoiceTextarea } from "@/components/VoiceInput";
import {
  Plus, Headset, Clock, CheckCircle2, AlertTriangle, MessageSquare,
  Camera, X, ChevronRight,
} from "lucide-react";

const CATEGORIES = [
  { value: "problema_plagas", label: "Reportar problema de plagas" },
  { value: "servicio_adicional", label: "Solicitud de servicio adicional" },
  { value: "consulta_general", label: "Consulta general" },
  { value: "peticion", label: "Petición" },
];

const PRIORITIES = [
  { value: "baja", label: "Baja", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "media", label: "Media", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "alta", label: "Alta", color: "bg-red-100 text-red-800 border-red-200" },
];

const STATUS_CONFIG = {
  abierto: { label: "Abierto", icon: Clock, color: "bg-amber-100 text-amber-800 border-amber-300" },
  en_proceso: { label: "En proceso", icon: MessageSquare, color: "bg-blue-100 text-blue-800 border-blue-300" },
  resuelto: { label: "Resuelto", icon: CheckCircle2, color: "bg-green-100 text-green-800 border-green-300" },
  cerrado: { label: "Cerrado", icon: CheckCircle2, color: "bg-zinc-100 text-zinc-600 border-zinc-300" },
};

const CATEGORY_LABELS = {
  problema_plagas: "Problema de plagas",
  servicio_adicional: "Servicio adicional",
  consulta_general: "Consulta general",
  peticion: "Petición",
};

export default function PortalSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);

  // Form
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("media");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/support-tickets/my");
      setTickets(res.data || []);
    } catch {
      toast.error("Error al cargar tickets");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setSubject("");
    setDescription("");
    setCategory("");
    setPriority("media");
    setPhotos([]);
  };

  const handlePhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) {
      toast.error("Máximo 5 fotos");
      return;
    }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos(prev => [...prev, ev.target.result]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!subject.trim()) return toast.error("Ingrese un asunto");
    if (!description.trim()) return toast.error("Ingrese una descripción");
    if (!category) return toast.error("Seleccione una categoría");
    setSubmitting(true);
    try {
      await api.post("/support-tickets", {
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
        photo_base64_list: photos,
      });
      toast.success("Ticket creado exitosamente");
      setShowCreate(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear ticket");
    } finally { setSubmitting(false); }
  };

  const openCount = tickets.filter(t => t.status === "abierto" || t.status === "en_proceso").length;

  return (
    <div data-testid="portal-support-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Soporte</h1>
          <p className="text-sm text-muted-foreground">Cree y administre sus tickets de soporte.</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowCreate(true); }}
          className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold shrink-0"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo ticket
        </Button>
      </div>

      {/* Summary */}
      {openCount > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4 flex items-center gap-2 text-sm text-amber-900" data-testid="open-tickets-banner">
          <Headset className="w-4 h-4" />
          <span>Tienes <strong>{openCount}</strong> ticket{openCount !== 1 ? "s" : ""} abierto{openCount !== 1 ? "s" : ""}</span>
        </div>
      )}

      {/* Ticket list */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : tickets.length === 0 ? (
        <Card className="p-10 text-center">
          <Headset className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-1">Sin tickets de soporte</p>
          <p className="text-xs text-muted-foreground">Cree un ticket si necesita asistencia.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map(t => {
            const st = STATUS_CONFIG[t.status] || STATUS_CONFIG.abierto;
            const StIcon = st.icon;
            const pr = PRIORITIES.find(p => p.value === t.priority);
            return (
              <Card
                key={t.id}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setShowDetail(t)}
                data-testid={`ticket-card-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    t.status === "abierto" ? "bg-amber-100" : t.status === "en_proceso" ? "bg-blue-100" : "bg-green-100"
                  }`}>
                    <StIcon className={`w-4 h-4 ${
                      t.status === "abierto" ? "text-amber-700" : t.status === "en_proceso" ? "text-blue-700" : "text-green-700"
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-sm font-semibold truncate">{t.subject}</span>
                      <Badge className={`text-[10px] px-1.5 py-0 ${st.color}`}>{st.label}</Badge>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {CATEGORY_LABELS[t.category] || t.category}
                      </Badge>
                      {pr && <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${pr.color}`}>{pr.label}</Badge>}
                      {t.photo_urls?.length > 0 && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Camera className="w-3 h-3" /> {t.photo_urls.length}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {new Date(t.created_at).toLocaleString("es-SV", { dateStyle: "medium", timeStyle: "short" })}
                      {t.odoo_ticket_id && <span className="ml-2 font-mono">#{t.odoo_ticket_id}</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ═══ CREATE TICKET DIALOG ═══ */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) setShowCreate(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headset className="w-5 h-5" /> Nuevo ticket de soporte
            </DialogTitle>
            <DialogDescription>
              Describa su solicitud y nuestro equipo le atenderá a la brevedad.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">Categoría</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-11" data-testid="ticket-category-select">
                  <SelectValue placeholder="Seleccione una categoría..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">Asunto</Label>
              <Input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Resumen breve de su solicitud"
                className="h-11"
                data-testid="ticket-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">Descripción</Label>
              <VoiceTextarea
                value={description}
                onChange={setDescription}
                placeholder="Describa el problema o solicitud en detalle..."
                rows={4}
                testId="ticket-description"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">Prioridad</Label>
              <div className="flex gap-2">
                {PRIORITIES.map(p => (
                  <button
                    key={p.value}
                    onClick={() => setPriority(p.value)}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      priority === p.value
                        ? `${p.color} border-current`
                        : "border-border text-muted-foreground hover:border-muted-foreground/40"
                    }`}
                    data-testid={`ticket-priority-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">Fotos (opcional, máx. 5)</Label>
              <div className="flex flex-wrap gap-2">
                {photos.map((src, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(i)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                {photos.length < 5 && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center hover:border-muted-foreground/40 transition-colors"
                    data-testid="ticket-add-photo"
                  >
                    <Camera className="w-5 h-5 text-muted-foreground" />
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhoto}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
              data-testid="ticket-submit-btn"
            >
              {submitting ? "Enviando..." : "Crear ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ TICKET DETAIL DIALOG ═══ */}
      <Dialog open={!!showDetail} onOpenChange={(o) => { if (!o) setShowDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {showDetail && (() => {
            const t = showDetail;
            const st = STATUS_CONFIG[t.status] || STATUS_CONFIG.abierto;
            const pr = PRIORITIES.find(p => p.value === t.priority);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-lg">{t.subject}</DialogTitle>
                  <DialogDescription className="flex items-center gap-2 flex-wrap pt-1">
                    <Badge className={`${st.color}`}>{st.label}</Badge>
                    <Badge variant="outline">{CATEGORY_LABELS[t.category]}</Badge>
                    {pr && <Badge variant="outline" className={pr.color}>{pr.label}</Badge>}
                    {t.odoo_ticket_id && <span className="text-xs font-mono text-muted-foreground">Ticket #{t.odoo_ticket_id}</span>}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Descripción</Label>
                    <p className="text-sm whitespace-pre-wrap mt-1">{t.description}</p>
                  </div>
                  {t.photo_urls?.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Fotos adjuntas</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {t.photo_urls.map((url, i) => (
                          <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block w-20 h-20 rounded-lg overflow-hidden border">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {t.admin_response && (
                    <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                      <Label className="text-xs text-blue-700 font-semibold">Respuesta del equipo</Label>
                      <p className="text-sm mt-1">{t.admin_response}</p>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Creado: {new Date(t.created_at).toLocaleString("es-SV", { dateStyle: "long", timeStyle: "short" })}
                    {t.resolved_at && <span className="block">Resuelto: {new Date(t.resolved_at).toLocaleString("es-SV", { dateStyle: "long", timeStyle: "short" })}</span>}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
